import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { useStrategy } from '../StrategyContext';
import Tip from '../components/Tip';

interface PerfMetrics {
  total_return: number;
  total_return_usd: number;
  win_rate: number;
  wins: number;
  losses: number;
  total_trades: number;
  profit_factor: number;
  max_drawdown: number;
  max_drawdown_usd: number;
  sharpe_ratio: number;
  avg_win: number;
  avg_loss: number;
  largest_win: number;
  largest_loss: number;
  avg_hold_win: number;
  avg_hold_loss: number;
}

interface CoinPerf {
  symbol: string;
  total_pnl_usd: number;
  total_pnl_pct: number;
  trades: number;
}

const Performance: React.FC = () => {
  const { strategy } = useStrategy();
  const [metrics, setMetrics] = useState<PerfMetrics | null>(null);
  const [coins, setCoins] = useState<CoinPerf[]>([]);
  const [mode, setMode] = useState('paper');
  const [side, setSide] = useState('');
  const [days, setDays] = useState<number | undefined>(undefined);
  const [strategyFilter, setStrategyFilter] = useState<string>('current');
  const [loading, setLoading] = useState(true);

  const effectiveStrategy = strategyFilter === 'all' ? undefined : strategy;

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.getPerformance(mode, days, side || undefined, effectiveStrategy).catch(() => null),
      api.getPerformanceByCoins(mode, side || undefined, effectiveStrategy).catch(() => []),
    ]).then(([perf, coinData]) => {
      setMetrics(perf);
      setCoins(coinData);
    }).finally(() => setLoading(false));
  }, [mode, side, days, strategy, strategyFilter]);

  const m = metrics || {
    total_return: 0, total_return_usd: 0, win_rate: 0, wins: 0, losses: 0,
    total_trades: 0, profit_factor: 0, max_drawdown: 0, max_drawdown_usd: 0,
    sharpe_ratio: 0, avg_win: 0, avg_loss: 0, largest_win: 0, largest_loss: 0,
    avg_hold_win: 0, avg_hold_loss: 0,
  };

  // Compute max abs pct for bar scaling
  const maxAbsPct = Math.max(...coins.map(c => Math.abs(c.total_pnl_pct)), 1);

  const pctColor = (v: number) => v >= 0 ? '#22c55e' : '#ef4444';

  const sideLabel = side === 'LONG' ? 'Spot' : side === 'FUTURES_LONG' ? 'Futures Long' : side === 'SHORT' ? 'Futures Short' : 'All';

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Performance Analytics</div>
        <div className="header-actions">
          <select className="filter-select" value={strategyFilter} onChange={e => setStrategyFilter(e.target.value)}>
            <option value="current">{strategy === 'donchian' ? 'Donchian' : 'UAlgo'}</option>
            <option value="all">All Strategies</option>
          </select>
          <select
            className="filter-select"
            value={mode}
            onChange={e => setMode(e.target.value)}
            style={{ fontWeight: 600, color: mode === 'paper' ? '#eab308' : '#22c55e', borderColor: mode === 'paper' ? 'rgba(234,179,8,0.25)' : 'rgba(34,197,94,0.25)', background: mode === 'paper' ? 'rgba(234,179,8,0.06)' : 'rgba(34,197,94,0.06)' }}
          >
            <option value="paper">Paper Results</option>
            <option value="live">Live Results</option>
          </select>
          <select
            className="filter-select"
            value={side}
            onChange={e => setSide(e.target.value)}
          >
            <option value="">All Sides</option>
            <option value="LONG">Spot (Long)</option>
            <option value="FUTURES_LONG">Futures Long</option>
            <option value="SHORT">Futures Short</option>
          </select>
          <select className="filter-select" value={days ?? 'all'} onChange={e => setDays(e.target.value === 'all' ? undefined : Number(e.target.value))}>
            <option value="all">All Time</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">YTD</option>
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontSize: 13, color: '#a1a1aa' }}>
        <span className={`badge badge-${mode}`}>{mode === 'paper' ? 'Paper Trading' : 'Live Trading'}</span>
        {side && (
          <span className="badge" style={side === 'SHORT'
            ? { background: 'rgba(168,85,247,0.15)', color: '#a855f7' }
            : side === 'FUTURES_LONG'
            ? { background: 'rgba(59,130,246,0.15)', color: '#3b82f6' }
            : { background: 'rgba(34,197,94,0.12)', color: '#22c55e' }
          }>
            {sideLabel}
          </span>
        )}
        {mode === 'paper' ? 'Showing simulated performance only. Switch to Live Results after connecting your Coinbase API.' : 'Showing live trading performance.'}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#a1a1aa' }}>Loading performance data...</div>
      ) : m.total_trades === 0 ? (
        <div className="card">
          <div className="card-body">
            <div className="empty-state">
              <div className="empty-state-icon">&#9650;</div>
              <div className="empty-state-text">No completed trades yet</div>
              <div className="empty-state-sub">Performance metrics will populate once the bot closes its first {side === 'SHORT' ? 'short' : side === 'LONG' ? 'long' : ''} trade.</div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="stats-row">
            <div className="stat-card">
              <div className="stat-label">Total Return <Tip text="Cumulative return from all closed trades as a percentage of starting capital" /></div>
              <div className={`stat-value ${m.total_return >= 0 ? 'green' : 'red'}`}>{m.total_return >= 0 ? '+' : ''}{m.total_return}%</div>
              <div className={`stat-sub ${m.total_return >= 0 ? 'green' : 'red'}`}>${m.total_return_usd.toLocaleString()}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Win Rate <Tip text="Percentage of closed trades that were profitable" /></div>
              <div className="stat-value">{m.win_rate}%</div>
              <div className="stat-sub gray">{m.wins} / {m.total_trades} trades</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Profit Factor <Tip text="Gross winnings divided by gross losses. Above 1.5 is strong, above 2.0 is excellent." /></div>
              <div className={`stat-value ${m.profit_factor >= 1.5 ? 'green' : m.profit_factor >= 1 ? '' : 'red'}`}>{m.profit_factor}</div>
              <div className="stat-sub gray">Gross W / Gross L</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Max Drawdown <Tip text="Largest peak-to-trough decline in portfolio value. Lower is better." /></div>
              <div className="stat-value yellow">-{m.max_drawdown}%</div>
              <div className="stat-sub gray">-${m.max_drawdown_usd.toLocaleString()} peak-to-trough</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Sharpe Ratio <Tip text="Risk-adjusted return. Above 1.0 is good, above 2.0 is excellent. Measures return per unit of risk." /></div>
              <div className={`stat-value ${m.sharpe_ratio > 0 ? 'green' : 'gray'}`}>{m.sharpe_ratio}</div>
              <div className="stat-sub gray">Risk-adjusted return</div>
            </div>
          </div>

          <div className="grid-equal">
            {/* Win/Loss Distribution */}
            <div className="card">
              <div className="card-header">
                <div className="card-title">Win/Loss Distribution <span className={`badge badge-${mode}`}>{mode === 'paper' ? 'Paper' : 'Live'}</span></div>
              </div>
              <div className="card-body">
                <div style={{ display: 'flex', gap: 4, height: 24, borderRadius: 6, overflow: 'hidden', marginBottom: 12 }}>
                  <div style={{ width: `${m.win_rate}%`, background: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#000' }}>{m.win_rate}% Win</div>
                  <div style={{ width: `${100 - m.win_rate}%`, background: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff' }}>{(100 - m.win_rate).toFixed(0)}% Loss</div>
                </div>
                <div className="grid-2col">
                  <div>
                    <div className="stat-label">Avg Win</div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: '#22c55e' }}>+{m.avg_win}%</div>
                  </div>
                  <div>
                    <div className="stat-label">Avg Loss</div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: '#ef4444' }}>{m.avg_loss}%</div>
                  </div>
                  <div>
                    <div className="stat-label">Largest Win</div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: '#22c55e' }}>+{m.largest_win}%</div>
                  </div>
                  <div>
                    <div className="stat-label">Largest Loss</div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: '#ef4444' }}>{m.largest_loss}%</div>
                  </div>
                  <div>
                    <div className="stat-label">Avg Hold (Win) <Tip text="Average number of days winning trades are held before closing" /></div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: '#e4e4e7' }}>{m.avg_hold_win}d</div>
                  </div>
                  <div>
                    <div className="stat-label">Avg Hold (Loss) <Tip text="Average number of days losing trades are held before being stopped out" /></div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: '#e4e4e7' }}>{m.avg_hold_loss}d</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Per-Coin Performance */}
            <div className="card">
              <div className="card-header">
                <div className="card-title">Per-Coin Performance <span className={`badge badge-${mode}`}>{mode === 'paper' ? 'Paper' : 'Live'}</span></div>
              </div>
              <div className="card-body">
                {coins.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-text">No coin data yet</div>
                  </div>
                ) : (
                  coins.map((c) => (
                    <div key={c.symbol} className="coin-perf-bar">
                      <span className="coin-perf-name">{c.symbol.replace('-USD', '')}</span>
                      <div className="coin-perf-track">
                        <div className="coin-perf-fill" style={{ width: `${Math.abs(c.total_pnl_pct) / maxAbsPct * 100}%`, background: pctColor(c.total_pnl_pct) }} />
                      </div>
                      <span className="coin-perf-val" style={{ color: pctColor(c.total_pnl_pct) }}>
                        {c.total_pnl_pct >= 0 ? '+' : ''}{c.total_pnl_pct}%
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Performance;
